import { AuctionBiddingEngine, Brand } from '@bonhams1793/bonhams-typescript'
import { differenceInDays, formatDistanceStrict } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { forwardRef, memo } from 'react'
import isEqual from 'react-fast-compare'

import { FireLink } from '@nx/fire/link'
import { LotCardItemProps } from '@nx/global-types'
import { buildAuctionRegistrationUrl, buildLotPath } from '@nx/helpers'
import { SmartlingAttributes } from '@nx/smartling'
import { getTranslate } from '@nx/translations'
import { useUserInteraction } from '@nx/utils/google-tag-manager'

import { useModals } from '@web/components/common/ModalManager/ModalContext'
import { useGetUser, useGetUserAddresses } from '@web/helpers/api'
import { useGetLaunchDarklyFlags } from '@web/helpers/api/launchDarkly'
import { useUserRegistrations } from '@web/helpers/api/user/useUserRegistrations'

import {
  LotCardContainer,
  LotCardMainContainer,
  StyledLotCardSeparator,
} from './LotCard.styles'
import translate from './LotCard.translate.json'
import { LotCardCompact } from './LotCardCompact'
import { LotCardAuctionMobility } from './components/LotCardAuctionMobility'
import { LotCardBiddingWidget } from './components/LotCardBiddingWidget'
import { LotCardImage } from './components/LotCardImage'
import { LotCardPrice } from './components/LotCardPrice'
import { LotCardStatus } from './components/LotCardStatus'
import { LotCardTitle } from './components/LotCardTitle'
import { useManageRegisterModal } from './useManageRegisterModal'

const { publicRuntimeConfig } = getConfig()
const { text } = getTranslate(translate)

export const LotCard = memo(
  forwardRef<HTMLDivElement, LotCardProps>(
    (
      {
        variant = 'normal',
        lot,
        isShowingLotId,
        lotClickCallback,
        showBidding,
        showActionButtons = true,
        isFollowButtonDisplayed,
        isBackTopShown,
        isUserPurchase = false,
        showPrice = true,
        lotTracking,
        ...props
      },
      ref
    ) => {
      const router = useRouter()
      const { data: user } = useGetUser()
      const { data: flags } = useGetLaunchDarklyFlags()

      const isAddressInRegistrationModalEnabled =
        !!flags?.['enable-address-in-registration-modal']

      const { data: userAddresses } = useGetUserAddresses({
        clientNo: user?.customerId,
      })

      const { dispatch } = useModals()

      function showLoginModal() {
        dispatch?.({
          type: 'OPEN',
          payload: {
            modal: 'LOGIN',
            props: {
              brand: lot.brand,
              user: null,
            },
          },
        })
      }

      function showBidModal() {
        dispatch?.({
          type: 'OPEN',
          payload: {
            modal: 'BID_NOW',
            props: {
              auctionId: String(lot.auctionId),
              lotId: lot.lotId,
              lotPageLink: href,
              trackingLocation: lotTracking.category,
            },
          },
        })
      }

      function showRegisterModal() {
        dispatch?.({
          type: 'OPEN',
          payload: {
            modal: 'REGISTER_NOW',
            props: {
              auctionId: lot.auctionId,
              lotUniqueId: lot.lotUniqueId,
              lotId: lot.lotId,
              isPreview: lot.isPreview,
              trackingLocation: lotTracking.category,
              showBidModal,
            },
          },
        })
      }

      const { recordInteraction } = useUserInteraction()
      const { userRegistrations } = useUserRegistrations()

      useManageRegisterModal({
        openRegistrationModal: showRegisterModal,
        lotUniqueId: lot.lotUniqueId,
        isLoggedIn: !!user,
      })

      const brandUrl: Record<string, string> = {
        [Brand.cars]: publicRuntimeConfig.CARS_BASE_URL,
      }
      const href = buildLotPath({
        auctionId: lot.auctionId,
        lotId: lot.isPreview ? lot.lotUniqueId : lot.lotId,
        lotName: lot.title,
        isPreview: lot.isPreview,
        domain: brandUrl[lot.brand],
        url: lot.url,
        extraUrlParams: lot.extraUrlParams,
      })

      let nowDate: Date | undefined = undefined
      let auctionEndDate: Date | undefined = undefined

      if (lot.hammerTime?.timezone) {
        nowDate = toZonedTime(new Date(), lot.hammerTime.timezone.iana)
        auctionEndDate = toZonedTime(
          lot.hammerTime.datetime,
          lot.hammerTime.timezone.iana
        )
      }

      function handleLotClick() {
        recordInteraction({
          action: 'lot-card.click',
          category: lotTracking.category,
          label: `${lot.auctionId}-${lot.isPreview ? lot.lotUniqueId : lot.lotId}`,
        })

        lotClickCallback?.()
      }

      return variant === 'compact' ? (
        <LotCardCompact
          ref={ref}
          image={lot.image}
          lotItemId={lot.lotItemId}
          lotIdNumber={lot.lotIdNumber}
          lotId={lot.lotId}
          biddingEngine={lot.biddingEngine}
          symbols={lot.symbols}
          title={lot.title}
          heading={lot.heading}
          status={lot.status}
          isResultPublished={lot.isResultPublished}
          isExhibition={lot.isExhibition}
          isPreview={lot.isPreview}
          isReferToDepartment={lot.isReferToDepartment}
          isWithoutReserve={lot.isWithoutReserve}
          currencySymbol={lot.currencySymbol}
          currencyISO={lot.currencyISO}
          hammerPrice={lot.hammerPrice}
          hammerPremium={lot.hammerPremium}
          estimateLow={lot.estimateLow}
          estimateHigh={lot.estimateHigh}
          GBPLowEstimate={lot.GBPLowEstimate}
          GBPHighEstimate={lot.GBPHighEstimate}
          auctionType={lot.auctionType}
          isShowingLotId={isShowingLotId}
          lotClickCallback={handleLotClick}
          isUserPurchase={isUserPurchase}
          href={href}
          showPrice={showPrice}
          {...props}
        />
      ) : (
        <LotCardMainContainer
          ref={ref}
          {...props}
          $isBackTopShown={isBackTopShown}
        >
          <LotCardContainer>
            <LotCardImage
              {...SmartlingAttributes.noTranslate}
              image={lot.image}
              lotItemId={lot.lotItemId}
              auctionId={lot.auctionId}
              title={lot.title}
              href={href}
              lotClickCallback={handleLotClick}
              isFollowButtonDisplayed={isFollowButtonDisplayed}
              biddingEngine={lot.biddingEngine}
            />
            <div>
              <FireLink href={href} onClick={handleLotClick}>
                <LotCardTitle
                  lotId={lot.lotId}
                  symbols={lot.symbols}
                  styledDescription={
                    lot.brand === Brand.carsOnline
                      ? text('onlineOnlyTitle', { title: lot.title })
                      : lot.styledDescription
                  }
                  title={lot.title}
                  heading={lot.heading}
                  isShowingLotId={isShowingLotId}
                  biddingEngine={lot.biddingEngine}
                  isExhibition={lot.isExhibition}
                  isPreview={lot.isPreview}
                />
                <LotCardStatus
                  status={lot.status}
                  isAmended={lot.isAmended}
                  isWithoutReserve={lot.isWithoutReserve}
                />
                {showPrice && (
                  <LotCardPrice
                    status={lot.status}
                    auctionType={lot.auctionType}
                    isResultPublished={lot.isResultPublished}
                    isExhibition={lot.isExhibition}
                    isReferToDepartment={lot.isReferToDepartment}
                    currencySymbol={lot.currencySymbol}
                    currencyISO={lot.currencyISO}
                    hammerPrice={lot.hammerPrice}
                    hammerPremium={lot.hammerPremium}
                    estimateLow={lot.estimateLow}
                    estimateHigh={lot.estimateHigh}
                    GBPLowEstimate={lot.GBPLowEstimate}
                    GBPHighEstimate={lot.GBPHighEstimate}
                    highlightWon={isUserPurchase}
                    showSoldPrice={
                      lot.biddingEngine !== AuctionBiddingEngine.BBE
                    }
                  />
                )}
              </FireLink>

              {lot.biddingEngine === AuctionBiddingEngine.AM ? (
                <LotCardAuctionMobility
                  showBidding={!!showBidding}
                  showActionButtons={!!showActionButtons}
                  showLoginModal={showLoginModal}
                  showRegisterModal={showRegisterModal}
                  showBidModal={showBidModal}
                  registerLink={buildAuctionRegistrationUrl({
                    rowId: `${lot.auctionAMId}`,
                    currentPath: router.asPath,
                    baseUrl: publicRuntimeConfig.WEB_BASE_URL,
                    baseAMUrl: publicRuntimeConfig.AM_PATHS['base'],
                  })}
                  isLoggedIn={!!user}
                  userHasAnAddress={
                    typeof userAddresses !== 'undefined'
                      ? !!userAddresses.length
                      : undefined
                  }
                  userHasBids={false}
                  userRegistrations={userRegistrations}
                  lot={lot}
                  auctionType={lot.auctionType}
                  lotClosingInString={
                    nowDate &&
                    auctionEndDate &&
                    differenceInDays(auctionEndDate, nowDate) > 0
                      ? text('closingTimer', {
                          numberOfDays: formatDistanceStrict(
                            nowDate,
                            auctionEndDate
                          ),
                        })
                      : undefined
                  }
                  hasHammerPrice={!!lot.hammerPrice}
                  isAddressInRegistrationModalEnabled={
                    isAddressInRegistrationModalEnabled
                  }
                />
              ) : lot.biddingEngine === AuctionBiddingEngine.BBE ? (
                <LotCardBiddingWidget
                  auction={{
                    auctionId: String(lot.auctionId),
                    auctionStatus: lot.auctionStatus,
                    auctionType: lot.auctionType,
                    biddingStatus: lot.biddingStatus,
                    startDate: lot.biddableFrom
                      ? {
                          ...lot.biddableFrom,
                          timestamp: 0,
                        }
                      : undefined,
                  }}
                  lot={{
                    biddingEngine: lot.biddingEngine,
                    brand: lot.brand,
                    currencySymbol: lot.currencySymbol,
                    hammerPremium: lot.hammerPremium,
                    hammerPrice: lot.hammerPrice,
                    hammerTime: lot.hammerTime
                      ? { ...lot.hammerTime, timestamp: 0 }
                      : undefined,
                    isExhibition: lot.isExhibition,
                    isPreviewLot: lot.isPreview,
                    isResultPublished: lot.isResultPublished,
                    isToBeSold: lot.isToBeSold,
                    lotNo: lot.lotId,
                    lotUniqueId: String(lot.lotUniqueId),
                    status: lot.status,
                  }}
                  showActionButtons={!!showActionButtons}
                  showBidding={!!showBidding}
                  showBidModal={showBidModal}
                  showLoginModal={showLoginModal}
                  showRegisterModal={showRegisterModal}
                  isAddressInRegistrationModalEnabled={
                    isAddressInRegistrationModalEnabled
                  }
                />
              ) : null}
            </div>
          </LotCardContainer>

          <StyledLotCardSeparator />
        </LotCardMainContainer>
      )
    }
  ),
  isEqual // deep comparison of props
)

export type LotCardProps = {
  variant?: 'normal' | 'compact'
  lot: LotCardItemProps
  lotClickCallback?: () => void
  isShowingLotId: boolean
  isFollowButtonDisplayed?: boolean
  isBackTopShown?: boolean
  isUserPurchase?: boolean
  showPrice?: boolean
  lotTracking: { category: string }
  showBidding?: boolean
  showActionButtons?: boolean
}
